import { get_token } from "@/router";
import { HOME_FEATURE_ICONS } from "@/utils/home_feature_icons";
import { get_active_organization_id } from "@/utils/organization_profile";

const HOME_WIDGET_STORAGE_KEY = "engagenet_home_widget_layout";

const ROLE_LABELS = {
  admin: "System admin",
  organization_admin: "Organisation admin",
  user: "Member",
};
const ADMIN_ONLY_WIDGET_KEYS = ["community_snapshot", "needs_attention"];
const ORGANISATION_ONLY_WIDGET_KEYS = ["community_snapshot", "needs_attention"];

export const FALLBACK_HOME_WIDGETS = [
  {
    key: "quick_access",
    title: "Quick access",
    label: "Recently used",
    size: "home-dashboard-widget-half",
  },
  {
    key: "platform_launcher",
    title: "Explore your EngageNet platform",
    label: "Sponsored",
    size: "home-dashboard-widget-wide",
  },
  {
    key: "marketplace",
    title: "Marketplace",
    label: "Commerce",
    size: "home-dashboard-widget-half",
  },
  {
    key: "discover_events",
    title: "Discover events",
    label: "Events",
    size: "home-dashboard-widget-half",
  },
  {
    key: "public_organizations",
    title: "Organisations you can join",
    label: "Discover",
    size: "home-dashboard-widget-half",
  },
  {
    key: "community_snapshot",
    title: "Community snapshot",
    label: "Live workspace",
    size: "home-dashboard-widget-half",
  },
  {
    key: "needs_attention",
    title: "Needs your attention",
    label: "2 actions",
    size: "home-dashboard-widget-half",
  },
];

function get_home_widget_definitions(vm) {
  return Array.isArray(vm.home_widget_definitions) && vm.home_widget_definitions.length
    ? vm.home_widget_definitions
    : FALLBACK_HOME_WIDGETS;
}

function get_home_widget_order(widget_definitions = []) {
  return widget_definitions.map(widget => widget.key);
}

function get_saved_home_widget_layout(storage_key) {

  return JSON.parse(localStorage.getItem(storage_key) || "{}");
}

function build_open_community_card(vm) {
  return ({
    show: vm.show_organisation_enabled,
    icon: "bi-grid-3x3-gap",
    icon_image: HOME_FEATURE_ICONS.groups,
    title: "Open community",
    description: `View ${vm.client_domain_label} Community`,
    action: `View ${vm.client_domain_label} Community`,
    href: "/feed",
  });
}

export const homeJourneyLayoutComputed = {
  normalized_user_role() {
    return this.normalize_role(this.active_user_details?.user_role);
  },
  role_key() {
    if (this.is_system_admin_user) return "admin";
    if (this.is_organization_admin_user) return "organization_admin";
    return "user";
  },
  is_system_admin_user() {
    return this.normalized_user_role === "admin";
  },
  is_admin_user() {
    return this.normalized_user_role.includes("admin");
  },
is_organization_admin_user() {
  return [
    "admin",
    "organization admin",
    "organisation admin",
  ].includes(this.normalized_user_role);
},
  is_standard_user() {
    return !this.is_admin_user;
  },
  can_manage_workspace() {
    return this.is_admin_user;
  },
  can_manage_organization() {
    return this.show_organisation_enabled && this.is_organization_admin_user;
  },
  has_active_organization() {
    const details = this.active_user_details || {};
    return Boolean(
      details.organization_id ||
      details.organisation_id ||
      get_active_organization_id()
    );
  },
  role_label() {
    return ROLE_LABELS[this.role_key] || ROLE_LABELS.user;
  },
  home_widgets: {
    get() {
      const widget_definitions = get_home_widget_definitions(this);
      return this.home_widget_order
        .filter(key => !this.hidden_home_widgets.includes(key))
        .map(key => widget_definitions.find(widget => widget.key === key))
        .filter(widget => this.can_show_home_widget(widget))
        .filter(Boolean);
    },
    set(widgets) {
      this.home_widget_order = widgets.map(widget => widget.key);
      this.persist_home_widget_layout();
    },
  },
  available_home_widgets() {
    return get_home_widget_definitions(this).filter(widget => this.can_show_home_widget(widget));
  },
  user_display_name() {
    const user = this.active_user_details || {};
    const full_name = [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
    return full_name || user.username || user.email || "there";
  },
  quick_access_cards() {
    const preferred_cards = [
      card => {
        const title = String(card.title || "").toLowerCase();
        return title.startsWith("create ") && title !== "create a post";
      },
      card => String(card.title || "").toLowerCase() === "create a post",
      card => String(card.title || "").toLowerCase() === "discover events",
      card => String(card.title || "").toLowerCase() === "community voice",
    ]
      .map(matcher => this.journey_cards.find(card => matcher(card)))
      .filter(Boolean);
    const quick_cards = preferred_cards.length ? preferred_cards : this.journey_cards;

    if (!this.token_exists) {
      return [
        ...(this.show_organisation_enabled ? [build_open_community_card(this)] : []),
        ...quick_cards.filter(card => card.title !== "Open community"),
      ].slice(0, 4);
    }

    return quick_cards.slice(0, 4);
  },
  platform_launcher_cards() {
    const quick_access_titles = new Set(
      this.quick_access_cards.map(card => String(card.title || "").toLowerCase())
    );
    return this.journey_cards
      .filter(card => !quick_access_titles.has(String(card.title || "").toLowerCase()))
      .slice(0, 9);
  },
  workspace_metrics() {
    return [
      { label: "Workspace Tools", value: this.active_client_features_count },
      ...(this.is_admin_user ? [{ label: "Companies", value: this.get_stat_total("companies") }] : []),
      { label: "Members", value: this.get_stat_total(["memberships", "profiles"]) },
      { label: "Upcoming events", value: this.get_stat_total("events") },
    ];
  },
  active_client_features_count() {
    return Object.entries(this.active_features || {})
      .filter(([key, value]) => key.startsWith("show_") && this.is_enabled_feature(value))
      .length;
  },
  setup_steps() {
    const setup_items = Array.isArray(this.organization_setup_items)
      ? this.organization_setup_items
      : [];
    const total = setup_items.length || 6;
    const completed = setup_items.filter(item => item.done).length;
    const remaining = Math.max(total - completed, 0);
    return ({
      completed,
      remaining,
      total,
      percent: Math.round((completed / total) * 100),
    });
  },
};

export const homeJourneyLayoutMethods = {
  normalize_role(role) {
    return String(role || "").trim().toLowerCase().replace(/[_-]+/g, " ");
  },
  get_home_widget_storage_key() {
    return HOME_WIDGET_STORAGE_KEY;
  },
  get_home_widget_api_url() {
    return `${process.env.VUE_APP_BACKEND_API_URL}/ads_management/home_widget_preferences/`;
  },
  get_home_widget_headers() {
    const token = get_token();
    return token
      ? {
        Authorization: `Token ${token}`,
        "Content-Type": "application/json",
      }
      : { "Content-Type": "application/json" };
  },
  get_home_widget_payload() {
    return {};
  },
  can_show_home_widget(widget = {}) {
    const key = String(widget.key || "");

    if (!key) {
      return false;
    }

    if (key === "discover_events" && !this.is_enabled_feature(this.active_features?.show_events)) {
      return false;
    }

    if (key === "marketplace" && !this.is_enabled_feature(this.active_features?.show_marketplace)) {
      return false;
    }

    if (key === "public_organizations") {
      return this.token_exists
        && this.show_organisation_enabled
        && this.is_standard_user
        && !this.has_active_organization;
    }

    if (ORGANISATION_ONLY_WIDGET_KEYS.includes(key) && !this.show_organisation_enabled) {
      return false;
    }

    if (ADMIN_ONLY_WIDGET_KEYS.includes(key) && !this.is_admin_user) {
      return false;
    }

    return true;
  },
  load_home_widget_layout() {
    const saved_layout = get_saved_home_widget_layout(this.get_home_widget_storage_key());
    const saved_order = Array.isArray(saved_layout.order) ? saved_layout.order : [];
    const saved_hidden = Array.isArray(saved_layout.hidden) ? saved_layout.hidden : [];
    const valid_keys = get_home_widget_order(get_home_widget_definitions(this));
    const next_order = [
      ...saved_order.filter(key => valid_keys.includes(key)),
      ...valid_keys.filter(key => !saved_order.includes(key)),
    ];

    this.home_widget_order = next_order;
    this.hidden_home_widgets = saved_hidden.filter(key => valid_keys.includes(key));
    this.fetch_home_widget_preferences();
  },
  persist_home_widget_layout() {
    localStorage.setItem(this.get_home_widget_storage_key(), JSON.stringify({
      order: this.home_widget_order,
      hidden: this.hidden_home_widgets,
    }));
    this.save_home_widget_preferences();
  },
  apply_home_widget_layout(layout = {}) {
    const valid_keys = get_home_widget_order(get_home_widget_definitions(this));
    const saved_order = Array.isArray(layout.order) ? layout.order : [];
    const saved_hidden = Array.isArray(layout.hidden) ? layout.hidden : [];
    this.home_widget_order = [
      ...saved_order.filter(key => valid_keys.includes(key)),
      ...valid_keys.filter(key => !saved_order.includes(key)),
    ];
    this.hidden_home_widgets = saved_hidden.filter(key => valid_keys.includes(key));
    localStorage.setItem(this.get_home_widget_storage_key(), JSON.stringify({
      order: this.home_widget_order,
      hidden: this.hidden_home_widgets,
    }));
  },
  apply_home_widget_api_response(response = {}) {
    const widgets = response?.data?.widgets;
    if (Array.isArray(widgets) && widgets.length) {
      this.home_widget_definitions = widgets;
    }

    this.apply_home_widget_layout(response?.data?.layout || {});
  },
  fetch_home_widget_preferences() {
    if (!get_token()) return;

    $.ajax({
      url: this.get_home_widget_api_url(),
      method: "GET",
      headers: this.get_home_widget_headers(),
      data: this.get_home_widget_payload(),
      success: (response) => {
        if (response?.status === "success") {
          this.apply_home_widget_api_response(response);
        }
      },
    });
  },
  save_home_widget_preferences() {
    if (!get_token()) return;

    $.ajax({
      url: this.get_home_widget_api_url(),
      method: "POST",
      headers: this.get_home_widget_headers(),
      data: JSON.stringify({
        ...this.get_home_widget_payload(),
        order: this.home_widget_order,
        hidden: this.hidden_home_widgets,
      }),
    });
  },
  reset_home_widget_layout() {
    this.home_widget_order = get_home_widget_order(get_home_widget_definitions(this));
    this.hidden_home_widgets = [];
    this.persist_home_widget_layout();
  },
  toggle_home_customizing() {
    this.is_customizing_home = !this.is_customizing_home;
  },
  is_home_widget_visible(key) {
    return !this.hidden_home_widgets.includes(key);
  },
  toggle_home_widget(key) {
    if (this.hidden_home_widgets.includes(key)) {
      this.hidden_home_widgets = this.hidden_home_widgets.filter(widget_key => widget_key !== key);
    } else {
      if (this.home_widgets.length <= 1) return;
      this.hidden_home_widgets = [...this.hidden_home_widgets, key];
    }

    this.persist_home_widget_layout();
  },
  get_stat_total(keys) {
    const stat_keys = Array.isArray(keys) ? keys : [keys];
    return stat_keys.reduce((total, key) => {
      return total + this.get_card_stats(key).reduce((sum, stat) => sum + (Number(stat.value) || 0), 0);
    }, 0);
  },
  open_launcher_menu(event) {
    event?.stopPropagation?.();
    const navbar = this.$refs.home_navbar;
    if (!navbar) return;

    if (event && !navbar.show_menu) {
      navbar.toggle_menu(event);
      return;
    }

    navbar.show_menu = true;
  },
};
